/**
 * Image upload functionality.
 */

window.tiptapp = window.tiptapp || {};

tiptapp.autocomplete = (function i() {
  const MAX_RESULTS = 6;
  const DEBOUNCE_MS = 180;
  const DETAILS_FIELDS = ['address_components', 'formatted_address', 'geometry', 'place_id'];
  const POWERED_BY_GOOGLE_IMG = 'https://storage.googleapis.com/geo-devrel-public-buckets/powered_by_google_on_white.png';

  const publicInterface = { init };

  let autocompleteService;
  let placesDetailsService;
  const contexts = [];
  let globalListenersAttached = false;

  function init() {
    if (!window.google || !google.maps || !google.maps.places) {
      return;
    }

    autocompleteService = new google.maps.places.AutocompleteService();
    placesDetailsService = new google.maps.places.PlacesService(document.createElement('div'));

    let onlyFromEstablishments = false;
    try {
      if (window.gVariant === 'store') {
        onlyFromEstablishments = true;
      }
    } catch (err) {
      onlyFromEstablishments = false;
    }

    setupAddrEventHandler('to-address');
    setupAddrEventHandler('from-address', onlyFromEstablishments);

    if (!globalListenersAttached) {
      globalListenersAttached = true;
      window.addEventListener('scroll', handleViewportChange, true);
      window.addEventListener('resize', handleViewportChange, true);
      document.addEventListener('click', handleGlobalClick, true);
    }

    hydratePrefilledAddress('to-address');
    hydratePrefilledAddress('from-address');
  }

  // For server-prefilled addresses (e.g. IKEA SH) that arrive without
  // coordinates: validation requires `loc.lat`/`loc.lng`, which are normally
  // attached when the user picks a Google Places suggestion. Resolve the
  // address text via the Places autocomplete service once on load so the
  // form passes validation without forcing the user to re-pick the same
  // address from the dropdown.
  function hydratePrefilledAddress(inputId) {
    console.log('running hydratePrefilledAddress()');
    const input = document.getElementById(inputId);
    if (!input || !input.value || !input.value.trim()) {
      return;
    }

    const attrAddress = parseAddressAttr(input);
    if (attrAddress && hasValidLoc(attrAddress)) {
      return;
    }

    const inputName = $(input).attr('name');
    const storedAddress = inputName ? tiptapp.store.address.get(inputName) : null;
    if (storedAddress && hasValidLoc(storedAddress)) {
      applyHydratedAddress(input, inputId, storedAddress);
      return;
    }

    const seed = attrAddress || storedAddress || {};
    resolveAddressViaPlaces(input.value, inputId, seed, location => {
      const merged = Object.assign({}, seed, {
        loc: { lat: location.lat(), lng: location.lng() },
      });
      applyHydratedAddress(input, inputId, merged);
    });
  }

  function resolveAddressViaPlaces(query, inputId, seed, onLocation) {
    console.log('running resolveAddressViaPlaces()');
    if (!autocompleteService || !placesDetailsService) {
      return;
    }

    const country = (window.gMarket && window.gMarket.countryCode ? window.gMarket.countryCode : (seed && seed.country) || '').toLowerCase();
    const request = {
      input: query,
      sessionToken: new google.maps.places.AutocompleteSessionToken(),
    };
    if (country) {
      request.componentRestrictions = { country: [country] };
    }
    const context = contexts.find(ctx => ctx.inputId === inputId);
    if (context && context.types && context.types.length) {
      request.types = context.types;
    }

    autocompleteService.getPlacePredictions(request, (predictions, status) => {
      if (status !== 'OK' || !predictions || !predictions.length) {
        return;
      }
      placesDetailsService.getDetails(
        { placeId: predictions[0].place_id, fields: ['geometry'], sessionToken: request.sessionToken },
        (place, detailsStatus) => {
          if (detailsStatus !== 'OK' || !place || !place.geometry || !place.geometry.location) {
            return;
          }
          onLocation(place.geometry.location);
        }
      );
    });
  }

  function applyHydratedAddress(input, inputId, address) {
    console.log('running applyHydratedAddress()');
    input.setAttribute('data-tiptappAddress', JSON.stringify(address));
    const inputName = $(input).attr('name');
    if (inputName) {
      tiptapp.store.address.set(inputName, address);
      tiptapp.store.input.remove(inputName);
    }
    // Deliberately skip the synthetic `change` event that a user-driven
    // dropdown selection dispatches. On page load that change cascades into
    // `handleInputUpdateEvent` re-writing `form|text|<name>` to session
    // storage; on the next load `reloadFormUsingStore` would replay it as a
    // synthetic `input` event and the autocomplete would open its
    // suggestion dropdown at (0,0) inside the still-collapsed accordion.
    if (tiptapp.validation && typeof tiptapp.validation.validateAddress === 'function') {
      tiptapp.validation.validateAddress(inputId);
    }
  }

  function parseAddressAttr(input) {
    console.log('running parseAddressAttr()');
    try {
      const raw = input.getAttribute('data-tiptappAddress');
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      return null;
    }
  }

  function hasValidLoc(address) {
    console.log('running hasValidLoc()');
    return Boolean(address && address.loc && typeof address.loc.lat === 'number' && typeof address.loc.lng === 'number');
  }

  function setupAddrEventHandler(inputId, onlyEstablishments = false) {
    const input = document.getElementById(inputId);
    if (!input) {
      return;
    }

    const suggestionUi = createSuggestionContainer(inputId);
    const context = {
      input,
      inputId,
      bounds: createMarketBounds(),
      types: resolveTypes(onlyEstablishments),
      predictions: [],
      highlightedIndex: -1,
      debounceTimer: null,
      sessionToken: new google.maps.places.AutocompleteSessionToken(),
      container: suggestionUi.container,
      listEl: suggestionUi.list,
      isOpen: false,
      hoverSuggestions: false,
      pendingSelection: false,
      countryCode: (window.gMarket && window.gMarket.countryCode ? window.gMarket.countryCode : 'SE').toLowerCase(),
    };

    contexts.push(context);

    attachInputAttributes(context);
    attachInputEvents(context);
  }

  function attachInputAttributes(context) {
    const { input, container } = context;
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-owns', container.id);
  }

  function attachInputEvents(context) {
    const { input, container } = context;
    input.addEventListener('input', () => handleInputChange(context));
    input.addEventListener('keydown', event => handleKeyDown(context, event));
    input.addEventListener('focus', () => handleFocus(context));
    input.addEventListener('blur', () => handleBlur(context));

    container.addEventListener('mouseenter', () => {
      context.hoverSuggestions = true;
    });
    container.addEventListener('mouseleave', () => {
      context.hoverSuggestions = false;
    });
  }

  function handleInputChange(context) {
    window.clearTimeout(context.debounceTimer);
    context.debounceTimer = window.setTimeout(() => requestPredictions(context), DEBOUNCE_MS);
  }

  function handleKeyDown(context, event) {
    if (!context.predictions.length) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveHighlight(context, 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveHighlight(context, -1);
        break;
      case 'Enter':
        event.preventDefault();
        selectPrediction(context, context.highlightedIndex >= 0 ? context.highlightedIndex : 0);
        break;
      case 'Escape':
        hideSuggestions(context);
        break;
      default:
        break;
    }
  }

  function handleFocus(context) {
    context.sessionToken = new google.maps.places.AutocompleteSessionToken();
    if (context.input.value && context.input.value.trim().length) {
      requestPredictions(context);
    }
  }

  function handleBlur(context) {
    window.setTimeout(() => {
      if (context.hoverSuggestions || context.pendingSelection) {
        return;
      }
      if (shouldAutoSelectFirst(context)) {
        selectPrediction(context, 0);
        return;
      }
      hideSuggestions(context);
    }, 120);
  }

  function shouldAutoSelectFirst(context) {
    return context.predictions.length > 0 && context.input.value && context.input.value.trim().length > 0;
  }

  function moveHighlight(context, delta) {
    if (!context.predictions.length) {
      return;
    }

    const nextIndex = (context.highlightedIndex + delta + context.predictions.length) % context.predictions.length;
    setHighlightedIndex(context, nextIndex);
  }

  function setHighlightedIndex(context, index) {
    if (context.highlightedIndex >= 0) {
      const prevEl = document.getElementById(`${context.container.id}-option-${context.highlightedIndex}`);
      if (prevEl) {
        prevEl.classList.remove('tt-autocomplete__item--active');
      }
    }

    context.highlightedIndex = index;
    const nextEl = document.getElementById(`${context.container.id}-option-${index}`);
    if (nextEl) {
      nextEl.classList.add('tt-autocomplete__item--active');
      context.input.setAttribute('aria-activedescendant', nextEl.id);
    }
  }

  function requestPredictions(context) {
    if (!autocompleteService) {
      return;
    }

    const value = context.input.value.trim();
    if (!value) {
      hideSuggestions(context);
      return;
    }

    const request = {
      input: value,
      sessionToken: context.sessionToken,
    };

    if (context.bounds) {
      request.bounds = context.bounds;
    }

    if (context.types && context.types.length) {
      request.types = context.types;
    }

    if (context.countryCode) {
      request.componentRestrictions = { country: [context.countryCode] };
    }

    autocompleteService.getPlacePredictions(request, (predictions, status) => {
      if (!isStatusOk(status) || !predictions || !predictions.length) {
        context.predictions = [];
        hideSuggestions(context);
        return;
      }

      context.predictions = predictions.slice(0, MAX_RESULTS);
      context.highlightedIndex = -1;
      renderPredictions(context);
    });
  }

  function renderPredictions(context) {
    context.listEl.innerHTML = '';

    if (!context.predictions.length) {
      hideSuggestions(context);
      return;
    }

    context.predictions.forEach((prediction, index) => {
      const item = document.createElement('div');
      item.className = 'tt-autocomplete__item';
      item.setAttribute('role', 'option');
      item.id = `${context.container.id}-option-${index}`;

      const mainRow = document.createElement('div');
      mainRow.className = 'tt-autocomplete__primary';
      mainRow.appendChild(
        createHighlightedFragment(prediction.structured_formatting.main_text, prediction.structured_formatting.main_text_matched_substrings)
      );
      item.appendChild(mainRow);

      if (prediction.structured_formatting.secondary_text) {
        const secondaryRow = document.createElement('div');
        secondaryRow.className = 'tt-autocomplete__secondary';
        secondaryRow.textContent = prediction.structured_formatting.secondary_text;
        item.appendChild(secondaryRow);
      }

      item.addEventListener('mousedown', event => {
        event.preventDefault();
        selectPrediction(context, index);
      });

      item.addEventListener('touchstart', event => {
        event.preventDefault();
        selectPrediction(context, index);
      });

      context.listEl.appendChild(item);
    });

    showSuggestions(context);
  }

  function showSuggestions(context) {
    context.isOpen = true;
    positionContainer(context);
    context.container.style.display = 'block';
    context.input.setAttribute('aria-expanded', 'true');
  }

  function hideSuggestions(context) {
    if (context.listEl) {
      context.listEl.innerHTML = '';
    }
    context.predictions = [];
    context.highlightedIndex = -1;
    context.isOpen = false;
    context.container.style.display = 'none';
    context.input.setAttribute('aria-expanded', 'false');
    context.input.removeAttribute('aria-activedescendant');
  }

  function selectPrediction(context, index) {
    const prediction = context.predictions[index];
    if (!prediction || !placesDetailsService) {
      hideSuggestions(context);
      return;
    }

    context.pendingSelection = true;
    hideSuggestions(context);

    placesDetailsService.getDetails(
      {
        placeId: prediction.place_id,
        fields: DETAILS_FIELDS,
        sessionToken: context.sessionToken,
      },
      (place, status) => {
        context.pendingSelection = false;
        if (!isStatusOk(status) || !place) {
          return;
        }
        applyPlaceToInput(place, context);
        context.sessionToken = new google.maps.places.AutocompleteSessionToken();
      }
    );
  }

  function applyPlaceToInput(place, context) {
    const { input, inputId, types } = context;
    if (!place || !place.address_components) {
      return;
    }

    const address = formatGooglePlaceToAddress(place);
    address.raw_components = getAddressComponentsFromPlace(place.address_components);

    const streetRegexp = (address.street || '').replace(/[|\\{}()[\]^$+*?.-]/g, '\\$&');
    let streetNoRegexp = `^${streetRegexp} `;
    if (window.gMarket && window.gMarket.countryCode === 'GB') {
      streetNoRegexp = `^([^ ]+) *${streetRegexp}`;
    }
    address.streetNo = address.streetNo || (input.value.match(streetNoRegexp) || [])[1];
    const name = getAddressNameFromComponents(address);
    if (window.gMarket && window.gMarket.countryCode === 'GB') {
      address.name = name.replace(new RegExp(streetRegexp), `${address.streetNo || ''} ${address.street || ''}`);
    } else {
      address.name = name.replace(new RegExp(`${streetRegexp}(?!\\s+\\d+)`), `${address.street || ''} ${address.streetNo || ''}`);
    }

    input.setAttribute('data-tiptappAddress', JSON.stringify(address));
    const inputName = $(`form :input#${inputId}`).attr('name');
    if (inputName) {
      tiptapp.store.address.set(inputName, address);
      tiptapp.store.input.remove(inputName);
    }
    $(`form :input#${inputId}`).trigger($.Event('change', { tiptappAddress: address }));
    tiptapp.validation.validateAddress(inputId);

    if (inputId === 'from-address') {
      tiptapp.validation.validateEqualAddress(inputId, 'to-address');
    }

    if (!types || !types.includes('establishment')) {
      if (address.street && address.street.trim()) {
        address.displayName = `${address.street} `;
      }

      if (address.displayName && address.streetNo && address.streetNo.trim()) {
        address.displayName += address.streetNo;
      }

      if (address.displayName && input.value.trim() !== address.displayName.trim()) {
        input.value = address.displayName;
      } else if (!address.displayName) {
        input.value = place.formatted_address || input.value;
      }
    } else {
      input.value = place.name || place.formatted_address || input.value;
    }
  }

  function createSuggestionContainer(inputId) {
    const container = document.createElement('div');
    container.className = 'tt-autocomplete';
    container.id = `tt-autocomplete-${inputId}`;
    container.style.display = 'none';
    container.setAttribute('role', 'listbox');

    const list = document.createElement('div');
    list.className = 'tt-autocomplete__list';
    container.appendChild(list);

    const footer = document.createElement('div');
    footer.className = 'tt-autocomplete__powered';
    const poweredImg = document.createElement('img');
    poweredImg.alt = 'Powered by Google';
    poweredImg.src = POWERED_BY_GOOGLE_IMG;
    footer.appendChild(poweredImg);
    container.appendChild(footer);

    document.body.appendChild(container);

    return { container, list };
  }

  function positionContainer(context) {
    const rect = context.input.getBoundingClientRect();
    context.container.style.minWidth = `${rect.width}px`;
    context.container.style.top = `${window.scrollY + rect.bottom}px`;
    context.container.style.left = `${window.scrollX + rect.left}px`;
  }

  function handleViewportChange() {
    contexts
      .filter(ctx => ctx.isOpen)
      .forEach(ctx => {
        positionContainer(ctx);
      });
  }

  function handleGlobalClick(event) {
    contexts.forEach(ctx => {
      if (ctx.container.contains(event.target) || ctx.input === event.target) {
        return;
      }
      hideSuggestions(ctx);
    });
  }

  function resolveTypes(onlyEstablishments) {
    if (onlyEstablishments) {
      return ['establishment'];
    }
    if (window.gMarket && window.gMarket.countryCode === 'GB') {
      return null;
    }
    return ['address'];
  }

  function createMarketBounds() {
    const centers = {
      GB: { lat: 51.507528, lng: -0.127791 },
      PT: { lat: 38.722252, lng: -9.139342 },
      DE: { lat: 52.5170365, lng: 13.3888599 },
    };

    const fallback = { lat: 59.324796, lng: 18.070586 };
    const country = window.gMarket && window.gMarket.countryCode;
    const center = centers[country] || fallback;

    const circle = new google.maps.Circle({
      center,
      radius: 70000,
    });

    return circle.getBounds();
  }

  function createHighlightedFragment(text, matches) {
    const fragment = document.createDocumentFragment();
    if (!text) {
      return fragment;
    }

    if (!matches || !matches.length) {
      fragment.appendChild(document.createTextNode(text));
      return fragment;
    }

    let lastIndex = 0;
    matches.forEach(match => {
      const start = match.offset;
      const end = start + match.length;
      if (start > lastIndex) {
        fragment.appendChild(document.createTextNode(text.substring(lastIndex, start)));
      }
      const highlight = document.createElement('span');
      highlight.className = 'tt-autocomplete__match';
      highlight.textContent = text.substring(start, end);
      fragment.appendChild(highlight);
      lastIndex = end;
    });

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
    }

    return fragment;
  }

  function isStatusOk(status) {
    const okStatus = google.maps.places.PlacesServiceStatus && google.maps.places.PlacesServiceStatus.OK;
    return status === 'OK' || status === okStatus;
  }

  return publicInterface;
})();
